import {
  Box,
  Button,
  Heading,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
  Spinner,
  VStack,
  Checkbox,
  CheckboxGroup,
  Textarea,
  Input,
  Image,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import { fetchQuestionPaperById } from "API/questionpaperAPI";
import { submitQuestionPaper } from "API/UserQuestionPaperApi";
import { useAppSelector } from "app/hook";
import { GoTrashcan } from "react-icons/go";

const AttemptExam = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const toast = useToast();
  const { user } = useAppSelector((state) => state.auth);

  const buildImageUrl = (img?: string): string | null => {
    if (!img || typeof img !== "string") return null;
    if (/^https?:\/\//i.test(img)) return img;
    const base = window.location.origin.replace(/:3000$/, ":9000").replace(/\/+$/, "");
    return `${base}/upload/${encodeURIComponent(img)}`;
  };

  // File validation constants
  const ALLOWED_TYPES = ["image/jpeg", "image/png"];
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

  const validateFile = (file: File | null): string | null => {
    if (!file) return null;
    if (!ALLOWED_TYPES.includes(file.type)) {
      return "Only JPG or PNG files allowed.";
    }
    if (file.size > MAX_FILE_SIZE) {
      return "File must be 2 MB or less.";
    }
    return null;
  };

  const handleFileChange = (questionId: string, file: File | null) => {
    const error = validateFile(file);
    setFileErrors((prev) => ({ ...prev, [questionId]: error }));
    setUploadedFiles((prev) => ({ ...prev, [questionId]: file }));
  };

  const removeFile = (questionId: string) => {
    setUploadedFiles((prev) => ({ ...prev, [questionId]: null }));
    setFileErrors((prev) => ({ ...prev, [questionId]: null }));
  };

  const { data, isLoading, error } = useQuery(
    ["questionPaper", id],
    () => fetchQuestionPaperById(id!),
    { enabled: !!id }
  );

  // Extract questions from sections structure
  const allQuestions = data?.sections?.flatMap((section: any) => 
    section.questions?.map((q: any) => ({
      ...q.questionId, // Spread the populated question data
      _id: q.questionId?._id,
      marks: q.marks,
      requiresStudentUpload: q.requiresStudentUpload, // ← ADD THIS LINE
      sectionName: section.name
    })) || []
  ) || [];

  console.log("=== DEBUG ATTEMPT EXAM ===");
  console.log("data:", data);
  console.log("sections:", data?.sections);
  console.log("allQuestions:", allQuestions);
  console.log("allQuestions length:", allQuestions.length);

  const [answers, setAnswers] = useState<{ [key: string]: any }>({});
  const [timeLeft, setTimeLeft] = useState<number>(-1); // -1 means not initialized yet
  const [timerStarted, setTimerStarted] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: string]: File | null }>({});
  const [fileErrors, setFileErrors] = useState<{ [key: string]: string | null }>({});
  
  // Get activeProgramId from Redux store or from user's activePrograms
  const { activeProgramId: reduxActiveProgramId } = useAppSelector((state) => state.auth);
  const getActiveProgramId = () => {
    if (reduxActiveProgramId) return reduxActiveProgramId;
    if (user?.activePrograms?.length > 0) return user.activePrograms[0]._id;
    if (data?.programId) return data.programId;
    return null;
  };

  // Set duration in seconds when paper loads
  useEffect(() => {
    if (data?.duration && timeLeft === -1) {
      setTimeLeft(data.duration * 60);
    }
  }, [data, timeLeft]);

  // Timer countdown effect - only start when timer is initialized and started
  useEffect(() => {
    if (timeLeft <= 0 && timerStarted) {
      if (data) {
        handleSubmit();
      }
      return;
    }
    
    if (timeLeft > 0 && timerStarted) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
    // eslint-disable-next-line
  }, [timeLeft, timerStarted]);

  // Start timer when component mounts and data is loaded
  useEffect(() => {
    if (data && timeLeft > 0 && !timerStarted) {
      setTimerStarted(true);
    }
  }, [data, timeLeft, timerStarted]);

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const { mutateAsync, isLoading: submitting } = useMutation(
    submitQuestionPaper,
    {
      onSuccess: () => {
        toast({
          title: "Submitted",
          description: "Your responses were submitted successfully.",
          status: "success",
        });
        history.push("/student/dashboard/attempt-review"); 
      },
      onError: (err: any) => {
        toast({
          title: "Submission Failed",
          description: err.message,
          status: "error",
        });
      },
    }
  );

  const handleAnswerChange = (questionId: string, answer: any) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = async () => {
    if (!data) return;

    // Validate files for questions that require upload
    for (const q of allQuestions) {
      if (q.requiresStudentUpload && !uploadedFiles[q._id]) {
        toast({
          title: "Validation Error",
          description: `Question "${q.question}" requires an answer image upload.`,
          status: "error",
        });
        return;
      }
      if (fileErrors[q._id]) {
        toast({
          title: "File Error",
          description: `Question "${q.question}": ${fileErrors[q._id]}`,
          status: "error",
        });
        return;
      }
    }

    const questions = allQuestions.map((q: any) => {
      const type = q.type?.toLowerCase();
      const hasTextAnswer = answers[q._id] !== undefined && answers[q._id] !== "";
      const hasFileUpload = uploadedFiles[q._id] !== null && uploadedFiles[q._id] !== undefined;
      const isAttempted = hasTextAnswer || hasFileUpload; // Mark as attempted if either text or file is provided
      
      return {
        question: q._id,
        attempted: isAttempted,
        mcqsSubmittedAnswer:
          type === "mcqs" ? Number(answers[q._id]) : undefined,
        mcqmSubmittedAnswers:
          type === "mcqm"
            ? (answers[q._id] || []).map((v: string) => Number(v))
            : undefined,
        tfSubmittedAnswer:
          type === "tf" ? Boolean(answers[q._id]) : undefined,
        shortSubmittedAnswer:
          type === "short" ? answers[q._id] : undefined,
        longSubmittedAnswer:
          type === "long" || type === "la" ? answers[q._id] : undefined,
        diagramSubmittedAnswer:
          type === "diagram" ? answers[q._id] : undefined,
        uploadedAnswer: uploadedFiles[q._id] ? q._id : undefined,
      };
    });

    const payload = {
      questionPaperId: id,
      questions,
      totalMarks: data.totalMarks,
      attemptedQuestion: questions.filter((q: any) => q.attempted).length,
      notAttemptedQuestion: questions.filter((q: any) => !q.attempted).length,
      correctAnswer: 0, 
      wrongAnswer: 0,
      timeTaken: data.duration * 60 - timeLeft,
      activeProgramId: getActiveProgramId(),
    };

    // Build FormData to include files
    const formData = new FormData();
    formData.append("payload", JSON.stringify(payload));
    
    // Debug logging
    console.log("=== FRONTEND SUBMIT ===");
    console.log("Payload object:", payload);
    console.log("Payload string length:", JSON.stringify(payload).length);
    console.log("Questions count:", questions.length);
    console.log("Uploaded files count:", Object.keys(uploadedFiles).filter(k => uploadedFiles[k]).length);
    
    // Add uploaded files
    Object.entries(uploadedFiles).forEach(([questionId, file]) => {
      if (file) {
        formData.append(`uploadedAnswer_${questionId}`, file);
        console.log(`Added file for question ${questionId}:`, file.name);
      }
    });

    console.log("FormData ready to send");
    await mutateAsync(formData as any);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Text color="red.500">
          Failed to load question paper or not found.
        </Text>
      </Box>
    );
  }

  if (!data?.sections || allQuestions.length === 0) {
    return (
      <Box>
        <Text color="red.500">No questions available.</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="md" mb={2}>
        {data.title || "Attempt Exam"}
      </Heading>
      <Text fontWeight="bold" mb={2}>Subject: {data.subject}</Text>
      <Text mb={4}>Total Marks: {data.totalMarks}</Text>

      {/* Timer display */}
      {timeLeft > 0 && (
        <Text
          fontSize="lg"
          fontWeight="bold"
          color={timeLeft <= 60 ? "red.500" : "green.600"}
          mb={4}
        >
          Time Left: {formatTime(timeLeft)}
        </Text>
      )}

      <VStack align="start" spacing={6}>
        {allQuestions.map((q: any, index: number) => {
          const type = q.type?.toLowerCase();
          return (
            <Box
              key={q._id}
              borderWidth="1px"
              p={4}
              borderRadius="md"
              w="100%"
            >
              <Text fontWeight="medium" mb={2}>
                Q{index + 1}. {q.question}
              </Text>
              <Text fontSize="sm" color="gray.500" mb={2}>
                Type: {type.toUpperCase()} | Marks: {q.marks} | Section: {q.sectionName}
              </Text>

              {/* Question Image */}
              {q.questionImg && (
                <Box mb={4} borderWidth="1px" borderRadius="md" p={2} bg="gray.50" display="flex" justifyContent="flex-start">
                  <Image 
                    src={buildImageUrl(q.questionImg)!} 
                    alt="Question" 
                    maxH="300px" 
                    objectFit="contain" 
                  />
                </Box>
              )}

              {/* Show answer fields when upload is NOT required */}
              {!q.requiresStudentUpload && (
                <>
                  {type === "mcqs" && (
                    <RadioGroup
                      onChange={(val) => handleAnswerChange(q._id, val)}
                      value={answers[q._id] ?? ""}
                    >
                      <Stack direction="column">
                        {q.mcqSOptions?.map((opt: string, i: number) => (
                          <Box key={i}>
                            <Radio value={i.toString()}>
                              {opt}
                            </Radio>
                            {q.mcqSOptionImages?.[i] && (
                              <Box ml={6} mt={2}>
                                <Image 
                                  src={buildImageUrl(q.mcqSOptionImages[i])!} 
                                  alt={`Option ${i + 1}`} 
                                  maxH="150px" 
                                  objectFit="contain" 
                                  borderWidth="1px"
                                  borderRadius="md"
                                />
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </RadioGroup>
                  )}

                  {type === "mcqm" && (
                    <CheckboxGroup
                      value={answers[q._id] || []}
                      onChange={(val) =>
                        handleAnswerChange(q._id, val) 
                      }
                    >
                      <Stack direction="column">
                        {q.mcqMOptions?.map((opt: string, i: number) => (
                          <Box key={i}>
                            <Checkbox value={i.toString()}>
                              {opt}
                            </Checkbox>
                            {q.mcqMOptionImages?.[i] && (
                              <Box ml={6} mt={2}>
                                <Image 
                                  src={buildImageUrl(q.mcqMOptionImages[i])!} 
                                  alt={`Option ${i + 1}`} 
                                  maxH="150px" 
                                  objectFit="contain" 
                                  borderWidth="1px"
                                  borderRadius="md"
                                />
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                  )}

                  {type === "tf" && (
                    <RadioGroup
                      onChange={(val) =>
                        handleAnswerChange(q._id, val === "true")
                      }
                      value={
                        answers[q._id] !== undefined
                          ? answers[q._id].toString()
                          : ""
                      }
                    >
                      <Stack direction="row" mt={2}>
                        <Radio value="true">True</Radio>
                        <Radio value="false">False</Radio>
                      </Stack>
                    </RadioGroup>
                  )}

                  {type === "la" && (
                    <Textarea
                      value={answers[q._id] || ""}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        handleAnswerChange(q._id, e.target.value)
                      }
                      placeholder="Enter your long answer"
                      mt={2}
                      minHeight="120px"
                    />
                  )}
                </>
              )}

              {/* Show upload field when required, AND show text fields for short/long/diagram types */}
              {q.requiresStudentUpload && (
                <>
                  {type === "short" && (
                    <Box mb={4}>
                      <FormLabel fontWeight="medium" mb={2}>Answer (Optional - provide text answer)</FormLabel>
                      <Input
                        type="text"
                        value={answers[q._id] || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleAnswerChange(q._id, e.target.value)
                        }
                        placeholder="Enter your short answer (optional)"
                        mt={2}
                      />
                    </Box>
                  )}

                  {type === "long" && (
                    <Box mb={4}>
                      <FormLabel fontWeight="medium" mb={2}>Answer (Optional - provide text answer)</FormLabel>
                      <Textarea
                        value={answers[q._id] || ""}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          handleAnswerChange(q._id, e.target.value)
                        }
                        placeholder="Enter your long answer (optional)"
                        mt={2}
                        minHeight="120px"
                      />
                    </Box>
                  )}

                  {type === "la" && (
                    <Box mb={4}>
                      <FormLabel fontWeight="medium" mb={2}>Answer (Optional - provide text answer)</FormLabel>
                      <Textarea
                        value={answers[q._id] || ""}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          handleAnswerChange(q._id, e.target.value)
                        }
                        placeholder="Enter your long answer (optional)"
                        mt={2}
                        minHeight="120px"
                      />
                    </Box>
                  )}

                  {type === "diagram" && (
                    <Box mb={4}>
                      <FormLabel fontWeight="medium" mb={2}>Answer (Optional - provide text description)</FormLabel>
                      <Textarea
                        value={answers[q._id] || ""}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          handleAnswerChange(q._id, e.target.value)
                        }
                        placeholder="Describe or explain the diagram (optional)"
                        mt={2}
                        minHeight="120px"
                      />
                    </Box>
                  )}
                </>
              )}

              {/* File Upload for questions that require student upload */}
              {q.requiresStudentUpload && (
                <Box mt={4} p={3} borderWidth="1px" borderColor="orange.300" borderRadius="md" bg="orange.50">
                  <FormControl isInvalid={Boolean(fileErrors[q._id])}>
                    <FormLabel fontWeight="bold" color="orange.700">
                      Upload Answer Image (PNG or JPG)
                    </FormLabel>
                    <HStack spacing={3} mt={2}>
                      <Input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={(e) => handleFileChange(q._id, e.target.files?.[0] || null)}
                        disabled={Boolean(uploadedFiles[q._id])}
                      />
                      {uploadedFiles[q._id] && (
                        <IconButton
                          aria-label="Remove file"
                          icon={<GoTrashcan />}
                          colorScheme="red"
                          size="sm"
                          onClick={() => removeFile(q._id)}
                        />
                      )}
                    </HStack>
                    {fileErrors[q._id] && (
                      <FormErrorMessage mt={2}>{fileErrors[q._id]}</FormErrorMessage>
                    )}
                    {uploadedFiles[q._id] && (
                      <Box mt={3}>
                        {uploadedFiles[q._id] && (
                          <>
                            <Text fontSize="sm" color="green.600" fontWeight="medium" mb={2}>
                              ✓ {uploadedFiles[q._id]!.name} ({(uploadedFiles[q._id]!.size / 1024).toFixed(2)} KB)
                            </Text>
                            <Image
                              src={URL.createObjectURL(uploadedFiles[q._id]!)}
                              alt="Uploaded answer"
                              maxH="200px"
                              objectFit="contain"
                              borderWidth="1px"
                              borderRadius="md"
                              borderColor="green.300"
                            />
                          </>
                        )}
                      </Box>
                    )}
                  </FormControl>
                </Box>
              )}
            </Box>
          );
        })}
      </VStack>

      <Button
        mt={8}
        colorScheme="teal"
        onClick={handleSubmit}
        isLoading={submitting}
      >
        Submit Exam
      </Button>
    </Box>
  );
};

export default AttemptExam;
